
import React, { useState } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CheckCircle, AlertTriangle, FileText, Users } from 'lucide-react';

interface ComplianceTabsProps {
  companyId: string;
  companyName: string;
  onAssessmentComplete: (results: any) => void;
}

const ComplianceTabs: React.FC<ComplianceTabsProps> = ({
  companyId,
  companyName,
  onAssessmentComplete
}) => {
  const [activeTab, setActiveTab] = useState('overview');

  const handleStartAssessment = () => {
    // Mock assessment completion
    const mockResults = {
      complianceScore: 85,
      completedDate: new Date(),
      recommendations: [
        'Implementar programa de bem-estar mental',
        'Treinar supervisores em primeiros socorros psicológicos',
        'Estabelecer canais de comunicação confidenciais'
      ]
    };
    
    onAssessmentComplete(mockResults);
  };

  return (
    <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
      <TabsList className="grid w-full grid-cols-4">
        <TabsTrigger value="overview">Visão Geral</TabsTrigger>
        <TabsTrigger value="assessment">Avaliação</TabsTrigger>
        <TabsTrigger value="training">Treinamentos</TabsTrigger>
        <TabsTrigger value="reports">Relatórios</TabsTrigger>
      </TabsList>

      <TabsContent value="overview" className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Status de Compliance</CardTitle>
              <CheckCircle className="h-4 w-4 text-green-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-600">85%</div>
              <p className="text-xs text-muted-foreground">Em conformidade</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Funcionários Treinados</CardTitle>
              <Users className="h-4 w-4 text-blue-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">120/150</div>
              <p className="text-xs text-muted-foreground">80% completo</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Ações Pendentes</CardTitle>
              <AlertTriangle className="h-4 w-4 text-orange-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-orange-600">3</div>
              <p className="text-xs text-muted-foreground">Requer atenção</p>
            </CardContent>
          </Card>
        </div>
      </TabsContent>

      <TabsContent value="assessment" className="space-y-4">
        <Card>
          <CardHeader>
            <CardTitle>Avaliação de Conformidade NR-1</CardTitle>
            <CardDescription>
              Avalie o nível de conformidade da sua empresa com as normas de saúde mental
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={handleStartAssessment} className="w-full">
              Iniciar Avaliação
            </Button>
          </CardContent>
        </Card>
      </TabsContent>

      <TabsContent value="training" className="space-y-4">
        <Card>
          <CardHeader>
            <CardTitle>Módulos de Treinamento</CardTitle>
            <CardDescription>
              Treinamentos obrigatórios para conformidade com a NR-1
            </CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-gray-600">
              Módulos de treinamento disponíveis para implementação.
            </p>
          </CardContent>
        </Card>
      </TabsContent>

      <TabsContent value="reports" className="space-y-4">
        <Card>
          <CardHeader>
            <CardTitle>Relatórios de Compliance</CardTitle>
            <CardDescription>
              Gere relatórios detalhados sobre o status de conformidade
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button variant="outline" className="w-full">
              <FileText className="h-4 w-4 mr-2" />
              Gerar Relatório
            </Button>
          </CardContent>
        </Card>
      </TabsContent>
    </Tabs>
  );
};

export default ComplianceTabs;
